import { taxData as configTaxData } from '../config';

//------------------------------------------------------------------------------

/**
 * Tax data.
 * @type {Object}
 */
export const taxes = window.taxData || configTaxData;

/**
 * Checks if number is within a specified range.
 * @param  {Number} num Number tested.
 * @param  {Number} min Minumum range value.
 * @param  {Number} max Maximum range value.
 * @return {Boolean}    True if within range, otherwise false.
 */
export const between = (num = 0, min = 0, max = 0) => num >= min && num <= max;

/**
 * Checks if an array has content.
 * @param  {Array}  arr  Array to check.
 * @return {Boolean}     True if we have content otherwise false.
 */
export const isArrayWithContent = (arr = []) => !!arr && arr[0] != null;

/**
 * Checks if an object has content.
 * @param  {Object} obj  Object to check.
 * @return {Boolean}     True if we have content otherwise false.
 */
export const isObjectWithContent = (obj = {}) =>
  obj.constructor === Object && Object.keys(obj).length > 0;

/**
 * Compares two Arrays and checks if they're identical.
 * @param  {Array} arr1 First array.
 * @param  {Array} arr2 Second array.
 * @return {Boolean}    True if identical, otherwise false.
 */
export const isEqualArrays = (arr1, arr2) => {
  const a = Array.isArray(arr1) ? arr1 : [];
  const b = Array.isArray(arr2) ? arr2 : [];
  const equalInLength = a.length === b.length;
  const equalInValues = a.every(el => b.includes(el));
  return equalInLength && equalInValues;
};

/**
 * Loops through and array of objects and collects specific values for each.
 * @param  {Array}  arr Array to search upon.
 * @param  {String} key Key of desired values.
 * @return {Object}     An object shaped like: { [object id]: [key value], ... }
 */
export const getObjectValuesFromArray = (arr = [], key) => {
  const obj = {};
  for (let i = 0; i < arr.length; i += 1) {
    obj[arr[i].id] = arr[i][key];
  }
  return obj;
};

/**
 * Finds the object within an array if the key and value match.
 * @param  {Array}  arr        Array to search upon.
 * @param  {String} key        Name of key you're trying to match.
 * @param  {String|Number} val Value of key you're trying to match.
 * @return {Object}            The object found otherwise 'undefined'.
 */
export const findArrayObjByValue = (arr = [], key, val) =>
  arr.find(obj => obj[key] === val);

/**
 * Finds and recursively removes all instances null, undefined,
 * and empty strings from an Object.
 * @param  {Object} obj Object to filter.
 * @return {Object}     Object with only defined values.
 */
export const removeEmptyObjValues = obj =>
  Object.keys(obj)
    .filter(f => obj[f] != null && obj[f] !== '')
    .reduce(
      (r, i) =>
        typeof obj[i] === 'object'
          ? { ...r, [i]: removeEmptyObjValues(obj[i]) }
          : { ...r, [i]: obj[i] },
      {},
    );

/**
 * Checks value to see if its a number or number-like.
 * @param  {Object} value Any unknown primitive.
 * @return {Boolean}      True if number-like, otherwise false.
 */
export const isNumber = value => !isNaN(parseFloat(value)) && isFinite(value);

/**
 * Converts localized number back into number primitive.
 * @param  {String} str Example: -$1,000.50
 * @return {Number}     Result: -1000.5
 */
export const toNumber = str => parseFloat(str.replace(/[^\d-.]/g, ''));

/**
 * Rounds a number up and converts it into USD format.
 * @param  {Number} num Example: -1000.5
 * @return {String}     Result: -$1,000
 */
export const formatAsCurrency = num => {
  const locales = 'en-US';
  const options = { style: 'decimal', maximumFractionDigits: 0 };
  const n = Math.ceil(num);
  return n >= 0
    ? `$${new Intl.NumberFormat(locales, options).format(n)}`
    : `-$${new Intl.NumberFormat(locales, options).format(Math.abs(n))}`;
};

/**
 * Get the future value of an investment.
 * @param  {Number} rate   The interest rate per period.
 * @param  {Number} nper   The total number of payment periods.
 * @param  {Number} pmt    The payment made each period.
 *                         Must be entered as a negative number.
 * @param  {Number} [pv]   The present value of future payments.
 *                         If omitted, assumed to be zero.
 *                         Must be entered as a negative number.
 * @param  {Number} [type] When payments are due.
 *                         0 = end of period (default),
 *                         1 = beginning of period.
 * @return {Number}        Future value.
 */
export const futureValue = (rate, nper, pmt, pv = 0, type = 0) => {
  const pow = (1 + rate) ** nper;
  let fv;
  if (rate) {
    fv = pmt * (1 + rate * type) * (1 - pow) / rate - pv * pow;
  } else {
    fv = -1 * (pv + pmt * nper);
  }
  return fv;
};



// WEBPACK FOOTER //
// ./src/utils/index.js